import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';

const selectVariants = cva(
  'w-full rounded-md border px-3 py-2 pr-8 text-sm transition-all duration-200 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none bg-background text-foreground shadow-input cursor-pointer',
  {
    variants: {
      variant: {
        default: 'border-input hover:border-ring/50 focus:ring-ring focus:border-transparent',
        error:
          'border-destructive hover:border-destructive/70 focus:ring-destructive focus:border-transparent',
      },
      size: {
        sm: 'h-9 text-xs px-2 pr-6 sm:h-8', // 36px minimum on mobile, 32px on desktop
        default: 'h-11 sm:h-10', // 44px minimum on mobile, 40px on desktop
        lg: 'h-12 px-4 pr-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    VariantProps<typeof selectVariants> {
  placeholder?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, variant, size, children, placeholder, ...props }, ref) => {
    return (
      <div className="relative">
        <select className={cn(selectVariants({ variant, size }), className)} ref={ref} {...props}>
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDownIcon
            className={cn(
              'text-muted-foreground transition-colors',
              size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'
            )}
          />
        </div>
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
